import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, User, MessageCircle, Lock, Flag } from 'lucide-react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import ReactionButton from '../components/ReactionButton';
import CommentSection from '../components/CommentSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

export default function PostDetail() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMembershipDialog, setShowMembershipDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/posts/${postId}`)
      .then(response => setPost(response.data))
      .catch(error => console.error('Error fetching post:', error))
      .finally(() => setLoading(false));
  }, [postId]);

  const handleConnect = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }

    if (!user.is_member) {
      setShowMembershipDialog(true);
      return;
    }

    try {
      await axios.post(`${API}/api/connections/${postId}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        withCredentials: true
      });
      toast.success('Connection request sent!');
      navigate('/messages');
    } catch (error) {
      console.error('Error creating connection:', error);
      toast.error('Failed to connect');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleReport = async () => {
    if (!reportReason.trim()) {
      toast.error('Please provide a reason for reporting');
      return;
    }

    try {
      await axios.post(`${API}/api/reports`, {
        reported_content_type: 'post',
        reported_content_id: postId,
        reason: reportReason
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        withCredentials: true
      });
      toast.success('Report submitted successfully');
      setShowReportDialog(false);
      setReportReason('');
    } catch (error) {
      console.error('Error reporting post:', error);
      toast.error('Failed to submit report');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Post not found</h2>
          <Button onClick={() => navigate('/home')}>Go Home</Button>
        </div>
      </div>
    );
  }

  const isOwnPost = user && post.user_id === user.user_id;

  return (
    <div className="min-h-screen bg-white">
      <nav className="border-b-4 border-black bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-20">
            <Button
              data-testid="back-button"
              onClick={() => navigate(-1)}
              variant="ghost"
              className="mr-4 border-2 border-black rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>Post Details</h1>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border-4 border-black rounded-3xl p-8 md:p-10 shadow-brutal-lg">
          <div className="flex items-start gap-4 mb-6">
            {post.user_picture ? (
              <img src={post.user_picture} alt={post.user_name} className="w-16 h-16 rounded-full border-2 border-black" />
            ) : (
              <div className="w-16 h-16 bg-indigo-100 rounded-full border-2 border-black flex items-center justify-center">
                <User className="w-8 h-8 text-indigo-500" />
              </div>
            )}
            <div className="flex-1">
              <h2 className="text-xl font-bold text-slate-900">{post.user_name}</h2>
              <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(post.created_at)}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {post.location.town}, {post.location.state}
                </span>
              </div>
            </div>
          </div>

          <Badge className="mb-4 border-2 border-black bg-lime-100 text-slate-900 font-bold">
            {post.category}
          </Badge>

          <h1 className="text-4xl font-black text-slate-900 mb-4" style={{fontFamily: 'Outfit'}}>
            {post.title}
          </h1>

          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            {post.description}
          </p>

          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {post.tags.map((tag, index) => (
                <Badge key={index} variant="outline" className="border-2 border-black">
                  #{tag}
                </Badge>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between border-t-2 border-black pt-6 mb-6">
            <ReactionButton postId={postId} />
            {user && !isOwnPost && (
              <Button
                onClick={() => setShowReportDialog(true)}
                variant="ghost"
                size="sm"
                className="text-slate-500 hover:text-red-500"
              >
                <Flag className="w-4 h-4 mr-2" />
                Report
              </Button>
            )}
          </div>

          {!isOwnPost && (
            <div className="border-t-2 border-black pt-6">
              <Button
                data-testid="connect-button"
                onClick={handleConnect}
                className="w-full md:w-auto bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold text-lg px-8 h-14 shadow-brutal"
              >
                {!user ? (
                  'Login to Connect'
                ) : !user.is_member ? (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    Membership required
                  </>
                ) : (
                  <>
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Connect with {post.user_name}
                  </>
                )}
              </Button>
              
              {user && !user.is_member && (
                <p className="text-sm text-slate-600 mt-3">
                  Membership required to connect with other members
                </p>
              )}
            </div>
          )}

          {isOwnPost && (
            <div className="border-t-2 border-black pt-6">
              <div className="bg-cyan-100 border-2 border-black rounded-xl p-4">
                <p className="font-bold text-slate-900">This is your post</p>
                <p className="text-sm text-slate-600 mt-1">Members can connect with you through this post</p>
              </div>
            </div>
          )}
        </div>

        {/* Comments Section */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="bg-white border-4 border-black rounded-3xl p-8 shadow-brutal-lg">
            <CommentSection postId={postId} />
          </div>
        </div>
      </div>

      <Dialog open={showMembershipDialog} onOpenChange={setShowMembershipDialog}>
        <DialogContent className="border-4 border-black rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black" style={{fontFamily: 'Outfit'}}>Membership Required</DialogTitle>
            <DialogDescription className="text-base">
              Get a LikeKind membership to connect with other members and send messages.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Button
              data-testid="get-membership-dialog-button"
              onClick={() => navigate('/membership')}
              className="w-full bg-pink-400 hover:bg-pink-500 text-white border-2 border-black rounded-full font-bold h-12 shadow-brutal"
            >
              🌟 Get Membership
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowMembershipDialog(false)}
              className="w-full border-2 border-black rounded-full font-bold"
            >
              Maybe Later
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="border-4 border-black rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black" style={{fontFamily: 'Outfit'}}>Report Post</DialogTitle>
            <DialogDescription className="text-base">
              Please let us know why you&apos;re reporting this post. We&apos;ll review it and take appropriate action.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Textarea
              placeholder="Please describe why you're reporting this post..."
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              className="border-2 border-black rounded-xl"
              rows={4}
            />
            <div className="flex gap-3">
              <Button
                onClick={handleReport}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white border-2 border-black rounded-full font-bold"
              >
                Submit Report
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowReportDialog(false);
                  setReportReason('');
                }}
                className="flex-1 border-2 border-black rounded-full font-bold"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}